import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";

const PANEL_MOTION = {
  initial: { height: 0, opacity: 0 },
  animate: { height: "auto", opacity: 1 },
  exit: { height: 0, opacity: 0 },
  transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] },
};

const FAQS = [
  {
    q: "What size businesses do you work with?",
    a: "We partner primarily with established small-to-mid-sized businesses doing $500K–$25M in annual revenue — owners who want to act like a national brand without the national-brand price tag. If you're earlier than that, we'll happily point you to resources that fit.",
  },
  {
    q: "How long until I see real results?",
    a: "Most clients see meaningful engagement and audience lift in the first 30–60 days, with measurable pipeline impact (leads, bookings, foot traffic) by month three. Compounding gains in months four through twelve are where the real ROI lives.",
  },
  {
    q: "Do you work on retainer or per-project?",
    a: "Both. Our flagship engagement is a monthly retainer (3-month minimum) because social and brand are compounding disciplines — but we also book one-time engagements for brand identity, website builds, and event coverage.",
  },
  {
    q: "Who actually creates the content?",
    a: "Your work is produced in-house by our Cincinnati-based team — strategists, designers, photographers, and editors who work under one roof. No offshoring, no white-labeling, no surprises.",
  },
  {
    q: "Will I own everything you create?",
    a: "Yes. Once an invoice is paid in full, you own 100% of the rights to every asset — raw files, source files, brand guidelines, the whole library. We just ask for credit when it's used publicly.",
  },
  {
    q: "How do you report results?",
    a: "Every client gets a custom monthly dashboard plus a written executive summary in plain English — what worked, what didn't, what we're changing next month, and which numbers tie to revenue. No vanity metrics.",
  },
  {
    q: "What if I'm not happy?",
    a: "We work on monthly cancellation terms after the initial 3-month period. We've never had a client cancel mid-quarter, but the option is yours — no penalties, no exit fees.",
  },
];

export default function FAQ() {
  const [open, setOpen] = useState(0);
  return (
    <section
      id="faq"
      className="relative bg-[#050B26] py-24 sm:py-32"
      data-testid="faq-section"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#5b80ff] font-semibold mb-4">
            FAQ
          </p>
          <h2 className="font-arial-black text-4xl sm:text-5xl lg:text-6xl text-white leading-tight tracking-tight">
            Answers, before you ask.
          </h2>
          <p className="text-white/65 max-w-2xl mx-auto mt-5 leading-relaxed">
            Common questions from owners and operators in their first
            conversation with us. Don&rsquo;t see yours? Drop a note below — we
            answer every one personally.
          </p>
        </div>

        <div className="border-t border-white/10">
          {FAQS.map((item, i) => {
            const isOpen = open === i;
            return (
              <div
                key={item.q}
                className="border-b border-white/10"
                data-testid={`faq-item-${i}`}
              >
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  className="w-full flex items-start justify-between gap-6 text-left py-6 sm:py-7 group"
                  aria-expanded={isOpen}
                  data-testid={`faq-toggle-${i}`}
                >
                  <span className="font-arial-bold text-lg sm:text-xl text-white leading-snug pr-2 group-hover:text-[#5b80ff] transition-colors">
                    {item.q}
                  </span>
                  <span
                    className={`size-9 shrink-0 rounded-full border flex items-center justify-center transition-all ${
                      isOpen
                        ? "bg-[#1440E1] border-[#1440E1] text-white rotate-45"
                        : "border-white/25 text-white group-hover:border-[#5b80ff] group-hover:text-[#5b80ff]"
                    }`}
                  >
                    <Plus size={16} strokeWidth={2.5} />
                  </span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      {...PANEL_MOTION}
                      className="overflow-hidden"
                    >
                      <p className="text-white/75 text-base leading-relaxed pb-7 pr-14 max-w-3xl">
                        {item.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
